import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  CREDIT_STATUS_COLOR,
  CREDIT_STATUS_LABEL,
  CREDIT_TYPE_LABEL,
  CreditStatus,
  CreditType,
  PARTNER_TYPE_LABEL,
  PartnerType,
  deleteCreditNote,
  fetchCreditNotes,
  refreshCreditNotesList,
} from "src/store/reducers/creditNotes";
import ConfirmationModal from "src/layouts/modals/ConfirmationModal";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import CreditNoteFormCanvas from "./CreditNoteFormCanvas";

const ListCreditNotes = () => {
  const dispatch = useAppDispatch();
  const creditNotesList = useAppSelector((state) => state.creditNotes.data);
  const listStatus = useAppSelector((state) => state.creditNotes.status);

  const [editId, setEditId] = useState("");
  const [deleteId, setDeleteId] = useState<string>();
  const [pending, setPending] = useState(false);

  const filterRef = useRef({});

  const { handleChange, values, handleSubmit, setFieldValue, resetForm } =
    useFormik({
      initialValues: {
        credit_type: "",
        status: "",
        partner_type: "",
        customer_number: "",
        from_date: "",
        to_date: "",
      },
      validationSchema: Yup.object({}),
      enableReinitialize: true,
      validateOnChange: false,
      onSubmit: (vals) => {
        setPending(true);
        const filter: Record<string, any> = {};
        if (vals.credit_type) filter.credit_type = Number(vals.credit_type);
        if (vals.status) filter.status = Number(vals.status);
        if (vals.partner_type) filter.partner_type = vals.partner_type;
        if (vals.customer_number) filter.customer_number = vals.customer_number;
        if (vals.from_date) filter.from_date = vals.from_date;
        if (vals.to_date) filter.to_date = vals.to_date;
        filterRef.current = filter;
        dispatch(fetchCreditNotes(filter)).finally(() => setPending(false));
      },
    });

  const retry = useCallback(() => {
    dispatch(fetchCreditNotes(filterRef.current));
  }, [dispatch]);

  const handleClearFilters = useCallback(() => {
    resetForm();
    filterRef.current = {};
    dispatch(fetchCreditNotes({}));
  }, [dispatch, resetForm]);

  useEffect(() => {
    if (listStatus === "idle") {
      retry();
    }
  }, [listStatus, retry]);

  useEffect(() => {
    return () => {
      dispatch(refreshCreditNotesList());
    };
  }, [dispatch]);

  const cancelDelete = () => setDeleteId(undefined);

  const confirmDelete = useCallback(async () => {
    if (!deleteId) return;
    try {
      await dispatch(deleteCreditNote(deleteId)).unwrap();
      toast.success("Credit note deleted successfully");
      setDeleteId(undefined);
      retry();
    } catch (e: any) {
      FormikAPIErrorHandler(e.message, undefined, toast.error);
    }
  }, [deleteId, dispatch, retry]);

  const editNote = creditNotesList.find((n) => n.id === editId);

  const isGivenFilter = values.credit_type === String(CreditType.Given);

  return (
    <>
      {/* Filters */}
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12">
            <div className="card h-auto">
              <div className="card-header">
                <h4 className="heading mb-0">Filter</h4>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="row container-fluid">
                  <div className="col-xl-3 mb-3">
                    <CustomSelectWithLabel
                      id="credit_type"
                      labelText="Credit Type"
                      handleChange={handleChange}
                      value={values.credit_type}
                    >
                      <option value="">All Types</option>
                      <option value={String(CreditType.Given)}>
                        {CREDIT_TYPE_LABEL[CreditType.Given]}
                      </option>
                      <option value={String(CreditType.Received)}>
                        {CREDIT_TYPE_LABEL[CreditType.Received]}
                      </option>
                    </CustomSelectWithLabel>
                  </div>
                  <div className="col-xl-3 mb-3">
                    <CustomSelectWithLabel
                      id="status"
                      labelText="Status"
                      handleChange={handleChange}
                      value={values.status}
                    >
                      <option value="">All Statuses</option>
                      {Object.entries(CREDIT_STATUS_LABEL).map(
                        ([val, label]) => (
                          <option value={val} key={val}>
                            {label}
                          </option>
                        )
                      )}
                    </CustomSelectWithLabel>
                  </div>
                  {values.credit_type === String(CreditType.Received) && (
                    <div className="col-xl-3 mb-3">
                      <CustomSelectWithLabel
                        id="partner_type"
                        labelText="Partner Type"
                        handleChange={handleChange}
                        value={values.partner_type}
                      >
                        <option value="">All Partners</option>
                        {Object.entries(PARTNER_TYPE_LABEL).map(
                          ([val, label]) => (
                            <option value={val} key={val}>
                              {label}
                            </option>
                          )
                        )}
                      </CustomSelectWithLabel>
                    </div>
                  )}
                  {isGivenFilter && (
                    <div className="col-xl-3 mb-3">
                      <CustomInputWithLabel
                        id="customer_number"
                        labelText="Customer Phone"
                        handleChange={handleChange}
                        value={values.customer_number}
                      />
                    </div>
                  )}
                  <div className="col-xl-3 mb-3">
                    <CustomDatePickerWithLabel
                      id="from_date"
                      labelText="From Date"
                      placeholder="From Date"
                      pickerType="YYYY-MM-DD"
                      handleChange={(d) => setFieldValue("from_date", d)}
                      value={values.from_date}
                      selectsStart
                      endDate={values.to_date}
                    />
                  </div>
                  <div className="col-xl-3 mb-3">
                    <CustomDatePickerWithLabel
                      id="to_date"
                      labelText="To Date"
                      placeholder="To Date"
                      pickerType="YYYY-MM-DD"
                      handleChange={(d) => setFieldValue("to_date", d)}
                      value={values.to_date}
                      selectsEnd
                      startDate={values.from_date}
                    />
                  </div>
                  <div className="col-xl-2 mb-3 d-flex gap-2 align-items-end">
                    <Button
                      className="form-control"
                      disabled={pending}
                      type="submit"
                    >
                      {pending ? "Loading" : "Search"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="form-control"
                      type="button"
                      onClick={handleClearFilters}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12">
            <div className="card h-auto">
              <div className="card-header">
                <h4 className="heading mb-0">Credit Notes</h4>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setEditId("Add")}
                >
                  + Add Credit Note
                </button>
              </div>
              <div className="card-body">
                {listStatus === "success" ? (
                  creditNotesList.length > 0 ? (
                    <div className="table-responsive active-projects">
                      <div className="dataTables_wrapper no-footer">
                        <table className="table table-hover dataTable no-footer mb-0">
                          <thead>
                            <tr>
                              <th>Reference</th>
                              <th>Type</th>
                              <th>Customer / Partner</th>
                              <th>Amount</th>
                              <th>Used</th>
                              <th>Remaining</th>
                              <th>Status</th>
                              <th>Expiry</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {creditNotesList.map((note) => {
                              const remaining =
                                parseFloat(note.amount) -
                                parseFloat(note.used_amount);
                              const canCancel =
                                note.status !== CreditStatus.Cancelled &&
                                note.status !== CreditStatus.Expired &&
                                note.status !== CreditStatus.FullyUsed;
                              return (
                                <tr key={note.id}>
                                  <td>
                                    <span className="text-primary fw-semibold">
                                      {note.reference_number}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge badge-sm ${
                                        note.credit_type === CreditType.Given
                                          ? "badge-outline-primary"
                                          : "badge-outline-info"
                                      }`}
                                    >
                                      {CREDIT_TYPE_LABEL[note.credit_type]}
                                    </span>
                                  </td>
                                  <td>
                                    {note.credit_type === CreditType.Given ? (
                                      <div>
                                        <div>{note.customer_name}</div>
                                        {note.customer_number && (
                                          <small className="text-muted">
                                            {note.customer_number}
                                          </small>
                                        )}
                                      </div>
                                    ) : (
                                      <div>
                                        <div>
                                          {note.partner_type
                                            ? PARTNER_TYPE_LABEL[
                                                note.partner_type as PartnerType
                                              ]
                                            : "—"}
                                        </div>
                                        {note.partner_name && (
                                          <small className="text-muted">
                                            {note.partner_name}
                                            {note.partner_city
                                              ? `, ${note.partner_city}`
                                              : ""}
                                          </small>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {note.currency} {parseFloat(note.amount).toLocaleString("en-IN")}
                                  </td>
                                  <td>
                                    {parseFloat(note.used_amount).toLocaleString("en-IN")}
                                  </td>
                                  <td>
                                    <strong>{remaining.toLocaleString("en-IN")}</strong>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge light badge-${
                                        CREDIT_STATUS_COLOR[note.status]
                                      }`}
                                    >
                                      {CREDIT_STATUS_LABEL[note.status]}
                                    </span>
                                  </td>
                                  <td>
                                    {note.expiry_date
                                      ? new Date(
                                          note.expiry_date
                                        ).toLocaleDateString("en-IN")
                                      : "—"}
                                  </td>
                                  <td>
                                    <NavLink
                                      className="btn btn-warning shadow btn-xs sharp me-1"
                                      to={`/credit-notes/view/${note.id}`}
                                      title="View"
                                    >
                                      <i className="fas fa-eye" />
                                    </NavLink>
                                    {canCancel && (
                                      <NavLink
                                        className="btn btn-primary shadow btn-xs sharp me-1"
                                        to={"#"}
                                        title="Edit"
                                        onClick={() => setEditId(note.id)}
                                      >
                                        <i className="fas fa-pen" />
                                      </NavLink>
                                    )}
                                    <NavLink
                                      className="btn btn-danger shadow btn-xs sharp"
                                      to={"#"}
                                      title="Delete"
                                      onClick={() => setDeleteId(note.id)}
                                    >
                                      <i className="fas fa-x" />
                                    </NavLink>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <strong>No credit notes found</strong>
                    </div>
                  )
                ) : listStatus === "pending" ? (
                  <div className="d-flex align-items-center">
                    <strong>Loading...</strong>
                    <div
                      className="spinner-border ms-auto"
                      role="status"
                      aria-hidden="true"
                    ></div>
                  </div>
                ) : listStatus === "error" ? (
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={retry}
                    >
                      Retry
                    </button>
                    &nbsp;
                    <strong>Please try again</strong>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={typeof deleteId !== "undefined"}
        handleClose={cancelDelete}
        confirmClose={confirmDelete}
      />

      <CreditNoteFormCanvas
        show={editId !== ""}
        hide={() => setEditId("")}
        editData={
          editId !== "Add"
            ? creditNotesList.find((n) => n.id === editId)
            : undefined
        }
        Title={editId === "Add" ? "Add Credit Note" : "Edit Credit Note"}
      />
    </>
  );
};

export default ListCreditNotes;
